import React, { useState } from 'react';
import { Mail, Lock, LogIn, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { login } from '../api/authApi';

/**
 * Login Page Component
 * Professional login page matching Sumadhura design
 */
const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      const data = await login(email, password);

      // Store auth state (persist token if API returns one)
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userEmail', email);
      if (data?.token) {
        localStorage.setItem('authToken', data.token);
      }
      if (data?.accessToken) {
        localStorage.setItem('authToken', data.accessToken);
      }

      // Multi-city: persist the user's allowed cities + super-admin flag so
      // CityContext can build the selector. Seed currentCity to the first
      // allowed city so the first paint already scopes correctly.
      const u = data?.user || {};
      const userCities = Array.isArray(u.cities) ? u.cities : [];
      localStorage.setItem('userCities', JSON.stringify(userCities));
      localStorage.setItem('isSuperAdmin', u.isSuperAdmin ? 'true' : 'false');
      if (!u.isSuperAdmin && userCities.length > 0) {
        localStorage.setItem('currentCity', userCities[0]);
      } else {
        localStorage.removeItem('currentCity'); // super-admin: CityContext picks
      }

      onLogin();
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const containerStyle = {
    display: 'flex',
    minHeight: '100vh',
    overflow: 'auto',
  };

  const imageSectionStyle = {
    display: 'none',
    width: '50%',
    flex: '0 0 50%',
    position: 'relative',
    minWidth: 0,
  };

  const imageStyle = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  const imageOverlayStyle = {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to bottom right, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.3), transparent)',
  };

  const imageContentStyle = {
    position: 'absolute',
    bottom: '48px',
    left: '48px',
    right: '48px',
    color: 'white',
  };

  const imageTitleStyle = {
    fontSize: '36px',
    fontFamily: '"Playfair Display", serif',
    marginBottom: '16px',
    lineHeight: '1.2',
    fontWeight: 600,
  };

  const imageTextStyle = {
    color: 'rgba(255, 255, 255, 0.8)',
    maxWidth: '384px',
    fontWeight: 300,
    lineHeight: '1.6',
  };

  const imageTopBadgeStyle = {
    position: 'absolute',
    top: '48px',
    left: '48px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const badgeLineStyle = {
    width: '40px',
    height: '1px',
    backgroundColor: '#c8a147',
  };

  const badgeTextStyle = {
    color: '#c8a147',
    letterSpacing: '0.15em',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
  };

  const formSectionStyle = {
    width: '100%',
    flex: '1 1 100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '24px',
    backgroundColor: '#ffffff',
    position: 'relative',
    minHeight: '100vh',
  };

  const formContainerStyle = {
    width: '100%',
    maxWidth: '448px',
  };

  const logoStyle = {
    fontFamily: '"Playfair Display", serif',
    fontSize: '36px',
    color: '#c8a147',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.15em',
    marginBottom: '40px',
    textAlign: 'center',
  };

  const logoUnderlineStyle = {
    height: '1px',
    width: '100%',
    backgroundColor: '#c8a147',
    marginTop: '4px',
    opacity: 0.6,
  };

  const welcomeTitleStyle = {
    fontSize: '32px',
    fontWeight: 700,
    color: '#111827',
    marginBottom: '8px',
  };

  const welcomeSubtitleStyle = {
    fontSize: '16px',
    color: '#6b7280',
    fontWeight: 300,
    marginBottom: '40px',
  };

  const formStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  };

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    marginBottom: '8px',
  };

  const inputContainerStyle = {
    position: 'relative',
  };

  const inputIconStyle = {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#9ca3af',
    zIndex: 1,
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px 12px 44px',
    fontSize: '15px',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    outline: 'none',
    transition: 'all 0.2s',
    boxSizing: 'border-box',
    backgroundColor: 'transparent',
  };

  const inputStyleWithRightIcon = {
    ...inputStyle,
    paddingRight: '44px',
  };

  const passwordToggleStyle = {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#9ca3af',
    zIndex: 1,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const errorStyle = {
    marginBottom: '20px',
    padding: '16px 20px',
    backgroundColor: '#fef2f2',
    border: '2px solid #f87171',
    borderRadius: '12px',
    color: '#b91c1c',
    fontSize: '15px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    boxShadow: '0 2px 8px rgba(185, 28, 28, 0.12)',
    lineHeight: 1.4,
  };
  const errorIconStyle = { flexShrink: 0, marginTop: '2px' };

  const submitButtonStyle = {
    width: '100%',
    padding: '16px 24px',
    fontSize: '16px',
    fontWeight: 600,
    background: 'linear-gradient(to right, #c8a147, #b8913f)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: isLoading ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    opacity: isLoading ? 0.7 : 1,
    boxShadow: '0 4px 6px rgba(200, 161, 71, 0.2)',
  };

  const watermarkStyle = {
    position: 'absolute',
    bottom: '32px',
    right: '32px',
    fontFamily: '"Playfair Display", serif',
    fontSize: '72px',
    color: '#c8a147',
    fontWeight: 700,
    textTransform: 'uppercase',
    opacity: 0.05,
    pointerEvents: 'none',
    userSelect: 'none',
    display: 'none',
  };

  return (
    <>
      <style>{`
        @media (min-width: 1024px) {
          .login-image-section {
            display: block !important;
            width: 50% !important;
            flex: 0 0 50% !important;
          }
          .login-form-section {
            width: 50% !important;
            flex: 0 0 50% !important;
          }
          .login-watermark {
            display: block !important;
          }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Playfair+Display:wght@600&display=swap');
        body {
          font-family: 'Inter', sans-serif;
        }
      `}</style>
      <div style={containerStyle}>
        {/* Image Section */}
        <div className="login-image-section" style={imageSectionStyle}>
          <img
            alt="Login"
            src="/poster.png"
            style={imageStyle}
          />
          <div style={imageOverlayStyle}></div>

          <div style={imageContentStyle}>
            <h2 style={imageTitleStyle}>
              Crafting Spaces of <br />Enduring Excellence
            </h2>
            <p style={imageTextStyle}>
              Experience the next generation of digital signage management. Secure, elegant, and designed for the professional enterprise.
            </p>
          </div>
        </div>

        {/* Form Section */}
        <div className="login-form-section" style={formSectionStyle}>
          <div style={formContainerStyle}>
            {/* Logo */}
            <div style={logoStyle}>
              Sumadhura
              <div style={logoUnderlineStyle}></div>
            </div>

            {/* Welcome Text */}
            <div>
              <h2 style={welcomeTitleStyle}>Welcome Back</h2>
              <p style={welcomeSubtitleStyle}>
                Sign in to access your digital signage dashboard.
              </p>
            </div>

            {/* Error Message - shown above form for visibility */}
            {error && (
              <div role="alert" style={errorStyle} aria-live="polite">
                <AlertCircle size={22} style={errorIconStyle} color="#b91c1c" />
                <span>{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} style={formStyle}>
              {/* Email */}
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="email">
                  Email Address
                </label>
                <div style={inputContainerStyle}>
                  <Mail size={20} style={inputIconStyle} />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@company.com"
                    style={inputStyle}
                    disabled={isLoading}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#c8a147';
                      e.target.style.boxShadow = '0 0 0 1px #c8a147';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="password">
                  Password
                </label>
                <div style={inputContainerStyle}>
                  <Lock size={20} style={inputIconStyle} />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    style={inputStyleWithRightIcon}
                    disabled={isLoading}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#c8a147';
                      e.target.style.boxShadow = '0 0 0 1px #c8a147';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    style={passwordToggleStyle}
                    onClick={() => setShowPassword((p) => !p)}
                    tabIndex={-1}
                    title={showPassword ? 'Hide password' : 'Show password'}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                style={submitButtonStyle}
                disabled={isLoading}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.target.style.background = 'linear-gradient(to right, #b8913f, #a17a35)';
                    e.target.style.transform = 'scale(0.98)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                    e.target.style.background = 'linear-gradient(to right, #c8a147, #b8913f)';
                    e.target.style.transform = 'scale(1)';
                  }
                }}
                onMouseDown={(e) => {
                  if (!isLoading) {
                    e.target.style.transform = 'scale(0.98)';
                  }
                }}
                onMouseUp={(e) => {
                  if (!isLoading) {
                    e.target.style.transform = 'scale(1)';
                  }
                }}
              >
                {isLoading ? (
                  <>
                    <div className="spinner" style={{
                      width: '18px',
                      height: '18px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite',
                      display: 'inline-block'
                    }}></div>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <LogIn size={18} />
                    <span>Sign In</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Watermark */}
          <div className="login-watermark" style={watermarkStyle}>
            Sumadhura
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
